/**
 * What you were typing in each chat, kept when you leave it and back when you return (ADMIN-212).
 * One localStorage entry holds them all, keyed by thread id; the oldest go once there are more
 * than `MAX_DRAFTS`. A new chat has no id until it's sent, so its draft isn't kept: the new tab
 * opens on an empty bar.
 */

const KEY = "kourai-drafts";
/** @public exported for its test */
export const MAX_DRAFTS = 50;

type Drafts = Record<string, { text: string; at: number }>;

function load(storage: Storage): Drafts {
  try {
    const parsed = JSON.parse(storage.getItem(KEY) ?? "{}");
    return parsed && typeof parsed === "object" ? (parsed as Drafts) : {};
  } catch {
    return {};
  }
}

function store(storage: Storage, drafts: Drafts) {
  try {
    if (Object.keys(drafts).length === 0) storage.removeItem(KEY);
    else storage.setItem(KEY, JSON.stringify(drafts));
  } catch {
    // Storage full or unavailable: the draft lives as long as the page.
  }
}

function defaultStorage(): Storage | undefined {
  try {
    return localStorage;
  } catch {
    return undefined;
  }
}

export function getDraft(threadId: string, storage = defaultStorage()): string {
  if (!storage) return "";
  return load(storage)[threadId]?.text ?? "";
}

/** Saves the thread's draft, or forgets it when empty. */
export function saveDraft(
  threadId: string,
  text: string,
  storage = defaultStorage(),
  now = Date.now(),
) {
  if (!storage) return;
  const drafts = load(storage);
  if (!text.trim()) {
    if (!(threadId in drafts)) return;
    delete drafts[threadId];
    store(storage, drafts);
    return;
  }
  drafts[threadId] = { text, at: now };
  const ids = Object.keys(drafts);
  if (ids.length > MAX_DRAFTS) {
    ids
      .sort((a, b) => (drafts[a]?.at ?? 0) - (drafts[b]?.at ?? 0))
      .slice(0, ids.length - MAX_DRAFTS)
      .forEach((id) => {
        delete drafts[id];
      });
  }
  store(storage, drafts);
}
